//#region libraries
import React, { useState, useEffect } from "react";
// bootstrap
import { Button, Row, Col, Container } from "react-bootstrap";
//styles
import { Colors, Spacing } from "_styles";
//custom components
import { CustomSpinner } from "_components/atoms";
import SurveyQuestion from "./survey-question";
import { translate } from "./surveys-utils";
import { isQuestionAnswered } from "./survey-question-types";
//#endregion

/**
 * Shared, prop-driven survey engine used by the passenger flow, the airport (CAA)
 * flow and the preview screen. Owns answers state, question navigation, validation
 * and the submit / completion lifecycle. It never touches redux itself — callers
 * pass flattened `questions` and an `onSubmit(answers)` that persists them.
 *
 * @param questions       flattened questions ({ text:"…", options:[…] }) for `language`
 * @param language        active language code (default "en")
 * @param onSubmit        async (answers) => void — persists answers. Skipped in preview.
 * @param renderCompleted () => ReactNode — screen shown once finished
 * @param previewMode     when true nothing is persisted; last step just shows completion
 */
const SurveyRunner = ({ questions = [], language = "en", onSubmit, renderCompleted, previewMode = false }) => {

    //#region local state
    const [answers, setAnswers] = useState({});
    const [questionNumber, setQuestionNumber] = useState(0);
    const [buttonTitle, setButtonTitle] = useState(translate('nextQuestion', language));
    const [errorQuestionMsg, setErrorQuestionMsg] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);
    //#endregion

    // Reset the button label whenever the language changes (e.g. preview language switch).
    useEffect(() => {
        const lastReached = questionNumber >= questions.length - 1 && questions.length > 0;
        setButtonTitle(translate(lastReached ? 'submitBtn' : 'nextQuestion', language));
    }, [language]);

    //#region finish / submit
    // When the user advances past the final question, persist (or, in preview, just finish).
    useEffect(() => {
        if (questions.length === 0) return;
        if (questionNumber !== questions.length) return;
        if (submitting || completed) return;

        if (previewMode) {
            setCompleted(true);
            return;
        }

        const finish = async () => {
            setSubmitting(true);
            setErrorQuestionMsg("");
            try {
                await onSubmit?.(answers);
                setCompleted(true);
            } catch (error) {
                console.error("Error submitting survey", error);
                setQuestionNumber(questions.length - 1);
                setErrorQuestionMsg(translate('pleaseSelectAnswer', language));
            } finally {
                setSubmitting(false);
            }
        };
        finish();
    }, [questionNumber, questions]);
    //#endregion

    //#region navigation + change handlers
    const handleNextQuestion = () => {
        setErrorQuestionMsg("");
        const currentQuestion = questions[questionNumber];
        const currentAnswer = answers[currentQuestion.questionId];

        if (!isQuestionAnswered(currentQuestion, currentAnswer)) {
            const msg = currentQuestion.type === 'matrix-scale5'
                ? translate('pleaseAnswerAllItems', language)
                : translate('pleaseSelectAnswer', language);
            setErrorQuestionMsg(msg);
            return;
        }

        setQuestionNumber((prev) => {
            const next = prev + 1;
            if (next === questions.length - 1) {
                setButtonTitle(translate('submitBtn', language));
            }
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAnswers((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e, option) => {
        const { name } = e.target;
        setAnswers((prev) => {
            const prevOptions = prev[name]?.options || [];
            const updatedOptions = e.target.checked
                ? [...prevOptions, option]
                : prevOptions.filter((opt) => opt !== option);
            return { ...prev, [name]: { ...prev[name], options: updatedOptions } };
        });
    };

    const handleCommentChange = (e) => {
        const { name, value } = e.target;
        setAnswers((prev) => ({ ...prev, [name]: { ...prev[name], comment: value } }));
    };

    const handleMatrixChange = (questionId, rowIndex, value) => {
        setAnswers((prev) => ({
            ...prev,
            [questionId]: { ...prev[questionId], [rowIndex]: value },
        }));
    };
    //#endregion

    //#region render
    const SubmittingScreen = () => (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", textAlign: "center", padding: 24 }}>
            <CustomSpinner size={'small'} />
            <p style={{ fontSize: 22, color: Colors.PRM_LIGHT_BLUE, opacity: 0.8, marginTop: 20, marginBottom: 8 }}>
                Submitting survey
            </p>
            <p style={{ fontSize: 16, opacity: 0.7, maxWidth: 320, margin: 0 }}>
                Please wait and do not close this tab.
            </p>
        </div>
    );

    if (completed) return <>{renderCompleted?.()}</>;
    if (submitting) return <SubmittingScreen />;

    const showingQuestions = questionNumber < questions.length && questions.length > 0;

    return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
            {showingQuestions ? (
                <>
                    <SurveyQuestion
                        index={questionNumber}
                        answers={answers}
                        question={questions[questionNumber]}
                        handleChange={handleChange}
                        handleCheckboxChange={handleCheckboxChange}
                        handleCommentChange={handleCommentChange}
                        handleMatrixChange={handleMatrixChange}
                        lang={language}
                    />
                    <Container style={{ textAlign: "center" }}>
                        <Row>
                            {errorQuestionMsg && <p className="text-danger">{errorQuestionMsg}</p>}
                            <Col xs={12} style={{ marginBottom: "30px" }}>
                                <Button variant="primary" onClick={handleNextQuestion} style={{ width: "100%" }}>
                                    {buttonTitle}
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </>
            ) : (
                <div style={{ marginLeft: Spacing.SCALE_12 }}>
                    <CustomSpinner size={'small'} style={{ marginTop: -Spacing.SCALE_18 }} />
                </div>
            )}
        </div>
    );
    //#endregion
};

export default SurveyRunner;
